<template>
  <div
    class="overflow-hidden bg-white rounded shadow flex flex-grow flex-col text-gray-800 text-left h-full"
    :class="{ detail: type === 'detail' }"
  >
    <div v-if="imageValues.length">
      <b-slideshow :images="imageValues" :title="title.value" />
    </div>
    <div class="h-full p-6 flex flex-col justify-between">
      <div class="whitespace-pre-wrap">
        <p class="font-semibold text-2xl">{{ title.value }}</p>
        <p class="text-base text-gray-700">{{ description.value }}</p>
      </div>
      <div>
        <a
          v-if="caption.value && caption.type === fieldTypes.URL"
          :href="caption.value"
          target="_blank"
          class="block mt-4 text-blue-500 hover:text-blue-800"
          @click.stop
        >
          Visit
          <b-icon class="ml-2" name="external-link-alt" />
        </a>
        <p v-else-if="caption.value" class="mt-4 text-gray-600 text-sm">
          {{ caption.value }}
        </p>
        <ul v-if="customFields.length && showCustomFields" class="mt-4">
          <li
            v-for="(field, index) in customFields"
            :key="index"
            class="flex justify-between"
          >
            <span class="font-bold">{{ field.name }}</span>
            <a
              v-if="
                field.value &&
                  (field.type === fieldTypes.URL ||
                    field.type === fieldTypes.IMAGE)
              "
              :href="field.value"
              target="_blank"
              class="text-blue-500 hover:text-blue-800"
              @click.stop
            >
              <b-icon class="ml-2" name="external-link-alt" />
            </a>
            <span v-else class="text-right">{{ field.value }}</span>
          </li>
        </ul>

        <slot name="bottom-content"> </slot>

        <button
          v-if="isCheckoutAction && ctaValue"
          class="w-full mt-4 bg-blue-500 hover:bg-blue-700 font-medium py-2 px-4 rounded text-center hover:shadow-md transition-shadow duration-300 focus:outline-none"
          :style="{
            'background-color': cta.bgColor,
            color: cta.color
          }"
          @click.self.prevent="ctaAction"
        >
          {{ cta.text }}
        </button>
        <a
          v-else-if="ctaValue"
          class="block mt-4 font-medium py-2 px-4 rounded text-center hover:shadow-md transition-shadow duration-300"
          :style="{
            'background-color': cta.bgColor,
            color: cta.color
          }"
          :href="ctaValue"
          :target="cta.download && cta.value ? '_self' : '_blank'"
          @click.stop
        >
          {{ cta.text }}
        </a>

        <slot name="footer"> </slot>
      </div>
    </div>
  </div>
</template>

<script>
import BIcon from "./BIcon";
import BSlideshow from "./BSlideshow";
import ActionTypes from "@/constants/action-types";
import FieldTypes from "@/constants/field-types";
import StringUtils from "@/utils/string";
import currency from "currency.js";

const captionField = "caption";
const ctaField = "cta";
const customFields = "custom";
const descriptionField = "description";
const imageField = "image";
const titleField = "title";

export default {
  name: "BContentCard",

  components: {
    BIcon,
    BSlideshow
  },

  props: {
    item: {
      type: Object,
      default: () => {}
    },
    config: {
      type: Object,
      default: () => {}
    },
    ctaAction: {
      type: Function,
      default: function() {
        this.$store.dispatch("checkout/selectItem", this.item);
      }
    },
    type: {
      type: String,
      default: "list",
      validator: val => ["list", "detail"].indexOf(val) > -1
    },
    showCustomFields: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      fieldTypes: FieldTypes
    };
  },

  computed: {
    cta() {
      return {
        text: this.config?.[ctaField]?.text || "",
        value: this.item[this.ctaFieldName] || "",
        bgColor: this.config?.[ctaField]?.bgColor || "#3398db",
        color: this.config?.[ctaField]?.color || "#ffffff",
        type: this.config?.[ctaField]?.type || ActionTypes.NUM_LINK
      };
    },
    ctaValue() {
      if (this.cta.download && this.cta.value) {
        return StringUtils.directDownloadLink(this.cta.value);
      }
      return this.cta.value;
    },
    ctaFieldName() {
      return this.config?.[ctaField]?.field?.name;
    },
    title() {
      return {
        value: this.item[this.config?.[titleField]?.name] || "",
        type: this.config?.[titleField]?.type || ""
      };
    },
    description() {
      return {
        value: this.item[this.config?.[descriptionField].name] || "",
        type: this.config?.[descriptionField]?.type || ""
      };
    },
    image() {
      const image = this.config?.[imageField];
      return {
        value: Array.isArray(image.name)
          ? image.name || []
          : [image.name] || [],
        type: image.type || ""
      };
    },
    imageValues() {
      return this.image.value.reduce((arr, el) => {
        const value = this.item[el];
        if (value) {
          const link = StringUtils.directDownloadLink(value) || value;
          arr.push(link);
        }
        return arr;
      }, []);
    },
    caption() {
      return {
        value: this.item[this.config?.[captionField]?.name] || "",
        type: this.config?.[captionField]?.type || ""
      };
    },
    customFields() {
      return (
        this.config?.[customFields]
          ?.map(el => ({
            name: el.name,
            value: this.item[el.name],
            type: el.type || ""
          }))
          .filter(el => {
            // Remove price fields from custom fields when $0.00
            if (
              el.name === this.ctaFieldName &&
              this.isCheckoutAction &&
              currency(this.ctaValue).value === 0
            ) {
              return false;
            } else if (el.value) {
              return true;
            } else {
              return false;
            }
          }) || []
      );
    },
    isCheckoutAction() {
      return this.cta?.type === ActionTypes.NUM_CHECKOUT;
    }
  },

  methods: {
    visitLink(link) {
      window.open(link);
    }
  }
};
</script>
